import { Schema } from "effect";
import type { Chart } from "../domain/models/chart";
import { t } from "../i18n/en";
import type { SocialProfile as SharedSocialProfile } from "../shared/api/friends";
import {
  FriendChartResponseSchema,
  FriendsDashboardResponseSchema,
  InvitePreviewResponseSchema,
  InviteUrlResponseSchema,
  PublicChartResponseSchema,
} from "../shared/api/friends-http";
import type { IncarnationCross } from "../utils/calculations";
import {
  getChartLibrarySession,
  getSavedChartDetails,
  getSavedChartSummariesForEmail,
  type SavedChartLibraryItem,
} from "./chart-library";

export interface FriendChart {
  id: string;
  chartName: string;
  energyType: string;
  authority: string;
  profile: string;
  incarnationCrossName: string;
  chart: Chart | null;
  incarnationCross: IncarnationCross | null;
  summary?: SavedChartLibraryItem["summary"];
}

export interface FriendSummary {
  friendshipId: string;
  connectedAt?: string;
  username: string;
  displayName: string;
  allowCompatibility: boolean;
  profilePhoto?: string;
  chart: FriendChart | null;
}

export type SocialProfile = SharedSocialProfile;

export interface FriendsDashboard {
  profile: SocialProfile | null;
  incoming: FriendSummary[];
  outgoing: FriendSummary[];
  friends: FriendSummary[];
  blocked: FriendSummary[];
  acceptedNotifications: FriendSummary[];
}

export interface ClaimChartPreview {
  chartName: string;
  phoneNumber?: string;
  birthDate: string;
  birthTime: string;
  location: string;
  energyType: string;
  authority: string;
  profile: string;
  incarnationCrossName: string;
  chart: SavedChartLibraryItem["chart"];
  incarnationCross: SavedChartLibraryItem["incarnationCross"];
  reportOutput: string;
}

export interface FriendInvitePreview {
  inviterName: string;
  kind: "connection" | "claim-chart";
  chart?: ClaimChartPreview;
}

export const FRIEND_ACCEPTANCES_SEEN_EVENT = "harness:friend-acceptances-seen";

const FRIENDS_DASHBOARD_CACHE_TTL_MS = 15_000;
const friendsDashboardCache = new Map<string, { dashboard: FriendsDashboard; expiresAt: number }>();
const friendsDashboardRequests = new Map<string, Promise<FriendsDashboard>>();

export class FriendsRequestError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = "FriendsRequestError";
    this.status = status;
  }
}

type JsonBody = null | boolean | number | string | JsonBody[] | { [key: string]: JsonBody };

function isJsonRecord(value: JsonBody): value is { [key: string]: JsonBody } {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function jsonString(value: JsonBody | undefined) {
  return typeof value === "string" ? value : "";
}

function normalizeFriendSummary(value: JsonBody): FriendSummary | null {
  if (!isJsonRecord(value)) return null;
  const chartValue = value.chart;
  let chart: FriendChart | null = null;
  if (isJsonRecord(chartValue)) {
    chart = {
      id: jsonString(chartValue.id),
      chartName: jsonString(chartValue.chartName),
      energyType: jsonString(chartValue.energyType),
      authority: jsonString(chartValue.authority),
      profile: jsonString(chartValue.profile),
      incarnationCrossName: jsonString(chartValue.incarnationCrossName),
      chart: chartValue.chart as FriendChart["chart"],
      incarnationCross: chartValue.incarnationCross as FriendChart["incarnationCross"],
      summary: undefined,
    };
  }
  return {
    friendshipId: jsonString(value.friendshipId),
    connectedAt: jsonString(value.connectedAt) || undefined,
    username: jsonString(value.username),
    displayName: jsonString(value.displayName),
    allowCompatibility: value.allowCompatibility === true,
    profilePhoto: jsonString(value.profilePhoto) || undefined,
    chart,
  };
}

function normalizeFriendsDashboard(value: JsonBody): FriendsDashboard | null {
  if (!isJsonRecord(value)) return null;
  const list = (key: string) =>
    Array.isArray(value[key])
      ? value[key]
          .map(normalizeFriendSummary)
          .filter((item): item is FriendSummary => item !== null)
      : [];
  const profile = isJsonRecord(value.profile)
    ? {
        username: jsonString(value.profile.username),
        displayName: jsonString(value.profile.displayName),
        primaryChartId: jsonString(value.profile.primaryChartId),
        allowCompatibility: value.profile.allowCompatibility === true,
        isPublic: value.profile.isPublic === true,
        profilePhoto: jsonString(value.profile.profilePhoto) || undefined,
      }
    : null;
  return {
    profile,
    incoming: list("incoming"),
    outgoing: list("outgoing"),
    friends: list("friends"),
    blocked: list("blocked"),
    acceptedNotifications: list("acceptedNotifications"),
  };
}

async function friendsRequest<T>(
  schema: Schema.Top,
  options: RequestInit = {},
  path = "/api/friends",
) {
  const session = getChartLibrarySession();
  if (!session) {
    throw new Error(t("ui.copy_sign_in_to_your_private_library_to_u_eb939e8c"));
  }
  const headers = new Headers(options.headers);
  if (!headers.has("Content-Type")) headers.set("Content-Type", "application/json");
  if (!headers.has("Authorization")) headers.set("Authorization", `Bearer ${session.token}`);

  const isRead = (options.method ?? "GET").toUpperCase() === "GET";
  const maxAttempts = isRead ? 2 : 1;
  let response: Response | undefined;

  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    response = await fetch(path, {
      ...options,
      headers,
    });

    const isTransientFailure = [502, 503, 504].includes(response.status);
    if (!isTransientFailure || attempt === maxAttempts) break;

    await new Promise((resolve) => window.setTimeout(resolve, 250));
  }

  if (!response) {
    throw new Error(t("ui.copy_the_friends_service_did_not_return_a_9e235773"));
  }
  const rawBody = await response.text();
  let data: JsonBody = null;

  if (rawBody) {
    try {
      data = JSON.parse(rawBody);
    } catch {
      throw new Error(t("ui.copy_the_friends_service_returned_an_unex_80e684d1"));
    }
  }

  if (!response.ok) {
    const errorMessage =
      typeof data === "object" && data !== null && "error" in data && typeof data.error === "string"
        ? data.error
        : "Could not update Harness friends.";
    throw new FriendsRequestError(errorMessage, response.status);
  }

  if (!data) {
    throw new Error(t("ui.copy_the_friends_service_returned_an_empt_970a7daf"));
  }

  try {
    const decode = Schema.decodeUnknownSync as (
      schema: Schema.Top,
    ) => (input: JsonBody) => JsonBody;
    return decode(schema)(data) as T;
  } catch (error) {
    if (schema === FriendsDashboardResponseSchema) {
      const normalized = normalizeFriendsDashboard(data);
      if (normalized) return normalized as T;
    }
    const message = error instanceof Error ? error.message : String(error);
    throw new Error(
      t("ui.copy_the_friends_service_returned_invalid_e8d3bf6b", { message: message }),
    );
  }
}

export function getFriendsDashboard() {
  const session = getChartLibrarySession();
  if (!session) return friendsRequest<FriendsDashboard>(FriendsDashboardResponseSchema);

  const key = session.email.trim().toLowerCase();
  const cached = friendsDashboardCache.get(key);
  if (cached && cached.expiresAt > Date.now()) return Promise.resolve(cached.dashboard);

  const existingRequest = friendsDashboardRequests.get(key);
  if (existingRequest) return existingRequest;

  const request = friendsRequest<FriendsDashboard>(FriendsDashboardResponseSchema)
    .then((dashboard) => {
      if (friendsDashboardRequests.get(key) === request) {
        friendsDashboardCache.set(key, {
          dashboard,
          expiresAt: Date.now() + FRIENDS_DASHBOARD_CACHE_TTL_MS,
        });
      }
      return dashboard;
    })
    .finally(() => {
      if (friendsDashboardRequests.get(key) === request) {
        friendsDashboardRequests.delete(key);
      }
    });

  friendsDashboardRequests.set(key, request);
  return request;
}

export function invalidateFriendsDashboardCache(email = getChartLibrarySession()?.email) {
  if (email) {
    const key = email.trim().toLowerCase();
    friendsDashboardCache.delete(key);
    friendsDashboardRequests.delete(key);
    return;
  }
  friendsDashboardCache.clear();
  friendsDashboardRequests.clear();
}

export function acknowledgeFriendAcceptances(friendshipIds: string[]) {
  invalidateFriendsDashboardCache();
  return friendsRequest<FriendsDashboard>(FriendsDashboardResponseSchema, {
    method: "POST",
    body: JSON.stringify({ action: "acknowledge-acceptances", friendshipIds }),
  }).finally(() => invalidateFriendsDashboardCache());
}

export function requestFriend(username: string) {
  invalidateFriendsDashboardCache();
  return friendsRequest<FriendsDashboard>(FriendsDashboardResponseSchema, {
    method: "POST",
    body: JSON.stringify({ action: "request", username }),
  }).finally(() => invalidateFriendsDashboardCache());
}

export function respondToFriendRequest(friendshipId: string, response: "accepted" | "declined") {
  invalidateFriendsDashboardCache();
  return friendsRequest<FriendsDashboard>(FriendsDashboardResponseSchema, {
    method: "POST",
    body: JSON.stringify({ action: "respond", friendshipId, response }),
  }).finally(() => invalidateFriendsDashboardCache());
}

export function removeFriend(friendshipId: string) {
  invalidateFriendsDashboardCache();
  return friendsRequest<FriendsDashboard>(FriendsDashboardResponseSchema, {
    method: "POST",
    body: JSON.stringify({ action: "remove", friendshipId }),
  }).finally(() => invalidateFriendsDashboardCache());
}

export function blockFriend(friendshipId: string) {
  invalidateFriendsDashboardCache();
  return friendsRequest<FriendsDashboard>(FriendsDashboardResponseSchema, {
    method: "POST",
    body: JSON.stringify({ action: "block", friendshipId }),
  }).finally(() => invalidateFriendsDashboardCache());
}

export function createFriendInvite() {
  return friendsRequest<{ inviteUrl: string }>(InviteUrlResponseSchema, {
    method: "POST",
    body: JSON.stringify({ action: "create-invite" }),
  });
}

export function createClaimFriendInvite(chartId: string) {
  return friendsRequest<{ inviteUrl: string }>(InviteUrlResponseSchema, {
    method: "POST",
    body: JSON.stringify({ action: "create-claim-invite", chartId }),
  });
}

export function acceptFriendInvite(token: string) {
  invalidateFriendsDashboardCache();
  return friendsRequest<FriendsDashboard>(FriendsDashboardResponseSchema, {
    method: "POST",
    body: JSON.stringify({ action: "accept-invite", token }),
  }).finally(() => invalidateFriendsDashboardCache());
}

export function claimFriendInvite(token: string) {
  invalidateFriendsDashboardCache();
  return friendsRequest<FriendsDashboard>(FriendsDashboardResponseSchema, {
    method: "POST",
    body: JSON.stringify({ action: "claim-invite", token }),
  }).finally(() => invalidateFriendsDashboardCache());
}

export function previewFriendInvite(token: string) {
  return friendsRequest<FriendInvitePreview>(InvitePreviewResponseSchema, {
    method: "POST",
    body: JSON.stringify({ action: "preview-invite", token }),
  });
}

export function friendChartAsLibraryItem(friend: FriendSummary): SavedChartLibraryItem | null {
  if (!friend.chart) return null;

  return {
    ...friend.chart,
    // Friend dashboards intentionally contain metadata only. The detail API
    // fills these fields when a chart is opened or selected for compatibility.
    chart: friend.chart.chart as SavedChartLibraryItem["chart"],
    incarnationCross: friend.chart.incarnationCross as SavedChartLibraryItem["incarnationCross"],
    id: `friend-${friend.username}-${friend.chart.id}`,
    username: friend.username,
    email: `friend:${friend.username}`,
    createdAt: new Date(0).toISOString(),
    birthDate: "",
    birthTime: "",
    location: "Private",
    reportOutput: "{}",
    profilePhoto: friend.profilePhoto,
    linkedFriendshipId: friend.friendshipId,
  };
}

function chartsHaveSameIdentity(first: SavedChartLibraryItem, second: SavedChartLibraryItem) {
  return (
    first.chartName.trim().toLowerCase() === second.chartName.trim().toLowerCase() &&
    first.birthDate === second.birthDate &&
    first.birthTime === second.birthTime &&
    first.location === second.location
  );
}

/**
 * Compatibility treats owned, saved, and shared friend charts as one collection.
 * Prefer the saved copy when a friend chart has already been stored locally or remotely.
 */
export async function getCompatibilityCharts() {
  const [savedCharts, dashboard] = await Promise.all([
    getSavedChartSummariesForEmail(),
    getFriendsDashboard(),
  ]);
  const charts = savedCharts.map((chart) =>
    chart.id === dashboard.profile?.primaryChartId && dashboard.profile.username
      ? { ...chart, username: dashboard.profile.username }
      : chart,
  );

  for (const friend of dashboard.friends) {
    const friendChart = friendChartAsLibraryItem(friend);
    if (!friendChart) continue;

    const alreadyAvailable = charts.some(
      (chart) =>
        chart.linkedFriendshipId === friendChart.linkedFriendshipId ||
        chartsHaveSameIdentity(chart, friendChart),
    );
    if (!alreadyAvailable) charts.push(friendChart);
  }

  return charts.sort((firstChart, secondChart) =>
    firstChart.chartName.localeCompare(secondChart.chartName, undefined, {
      sensitivity: "base",
      numeric: true,
    }),
  );
}

export async function getCompatibilityChartDetails(chart: SavedChartLibraryItem) {
  if (chart.linkedFriendshipId) {
    return getFriendChartDetails(chart.linkedFriendshipId);
  }
  return chart.chart ? chart : getSavedChartDetails(chart.id);
}

export async function getFriendChartDetails(friendshipId: string) {
  const data = await friendsRequest<{ chart: SavedChartLibraryItem }>(
    FriendChartResponseSchema,
    {},
    `/api/friends?friendshipId=${encodeURIComponent(friendshipId)}`,
  );
  return data.chart;
}

export async function getFriendChartDetailsByUsername(username: string) {
  const data = await friendsRequest<{ chart: SavedChartLibraryItem }>(
    FriendChartResponseSchema,
    {},
    `/api/friends?username=${encodeURIComponent(username)}`,
  );
  return data.chart;
}

export async function getPublicChartDetailsByUsername(username: string) {
  // Public chart URLs must use the public API branch even when the viewer is
  // signed in. Sending the private session token makes the server interpret
  // this as a friendship lookup and reject otherwise-public charts.
  const response = await fetch(`/api/friends?username=${encodeURIComponent(username)}`);
  const rawBody = await response.text();
  let data: JsonBody = {};
  try {
    data = rawBody ? JSON.parse(rawBody) : {};
  } catch {
    throw new Error(t("ui.copy_the_public_chart_service_returned_an_4d237950"));
  }
  if (!response.ok) {
    const errorMessage =
      typeof data === "object" && data !== null && "error" in data && typeof data.error === "string"
        ? data.error
        : "That public chart is not available.";
    throw new FriendsRequestError(errorMessage, response.status);
  }

  const publicChart = Schema.decodeUnknownSync(PublicChartResponseSchema)(data);
  if (!publicChart.chart.chart || !publicChart.chart.incarnationCross) {
    throw new Error(t("ui.copy_the_public_chart_service_returned_in_e38080c1"));
  }

  return {
    id: `public-${publicChart.username || username}`,
    email: `public:${publicChart.username || username}`,
    createdAt: new Date(0).toISOString(),
    birthDate: "",
    birthTime: "",
    location: "Public",
    chartName: publicChart.chart.chartName,
    energyType: publicChart.chart.energyType,
    authority: publicChart.chart.authority,
    profile: publicChart.chart.profile,
    incarnationCrossName: publicChart.chart.incarnationCrossName,
    chart: publicChart.chart.chart,
    incarnationCross: publicChart.chart.incarnationCross,
    reportOutput: "",
    summary: publicChart.chart.summary ?? undefined,
  } satisfies SavedChartLibraryItem;
}
